import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import { useTranslation } from "react-i18next";
import view from './DocumentPage.view';
import {FILE_TYPE_MODEL, FILE_TYPE_SIDE_MODEL} from "../../../Dashboard/Visitor/Visitor/Model/Model";
import {
    GetUserRestrictionVisitor,
    ValidateObverseDocument,
    ValidateReverseDocument,
    VeridasServiceProcess
} from "../../../../reduxToolkit/Visitor/VisitorApi";
import { setIsSuccessValidation, setUserDataDocument } from "../../../../reduxToolkit/Visitor/VisitorSlice";
import { toast } from "react-toastify";
import {
    getFailedUrl,
    getSuccessUrl,
    getSdkLanguage,
    loadVdSdk,
    DEVICE_ROTATED_ON_ORIENTATION
} from "../../Model/Models";
import FullPageLoader from '../../../../utils/FullPageLoader';
import routes from '../../../../Routes/routes';
import {colors} from '../../../../enums/colors';
import useHandleBeforeUnload from '../../Functionality/useHandleBeforeUnload';
import useBackNavigation from '../../Functionality/useBackNavigation';
import {getUrlParameters} from "../../../Dashboard/Visitor/External/Model/Model";
import {
    registerInEventVeridas
} from "../../../Dashboard/Events/External/Model/Model";

/**
 * @component DocumentPage
 * @description Handles the document capture flow using the Veridas VDDocument SDK.
 * Supports INE (obverse + reverse) and Passport (obverse only) document types,
 * determined by `selectedDocumentType` from Redux store.
 *
 * @behavior
 * - Pre-fetches required SDK assets (tf.worker, tfjs, wasm) in parallel on mount
 *   to validate they are reachable before the SDK initializes.
 * - Loads `vd-document.umd.js` once `documentType` is set, then injects `<vd-document>`
 *   into the DOM container and calls `initializeSdk`.
 * - On `VD_capture`, routes the image to `obverseImage` or `reverseImage` state
 *   based on the `legacyEvent` field (`VDDocument_obverseDetection` / `VDDocument_reverseDetection`).
 * - Capture completion is detected via the `obverseImage`/`reverseImage` useEffect:
 *   INE requires both sides; Passport requires obverse only.
 * - On success, navigates to the selfie step if `biometricVisitor` is enabled,
 *   otherwise to `successUrl` (or registers in event via `registerInEventVeridas` if `eventId` present).
 * - On `VD_closeButtonClicked`, calls `handleLeave` to abort and navigate away.
 *
 * @param {string} id - Route param: validation/invitation ID used to build the selfie redirect URL.
 * @param {string} [guardFlowId] - Route param: guard flow context.
 * @param {string} [userId] - Route param: visitor user ID.
 * @param {string} [eventId] - Route param: event ID, triggers `registerInEventVeridas` on success.
 *
 * @author Cecilia López Martínez <clopez@ibl.mx> — IBL Full Stack Developer
 */
const DocumentPage = () => {
    const { id, guardFlowId, userId, eventId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const targetRef = useRef(null);

    const primaryColor = colors.primary800;
    const whiteColor = colors.general00;

    const selectedDocumentType = useSelector((state) => state.VisitorSlice.selectedDocumentType);
    const idProcess = useSelector((state) => state.VisitorSlice.getValidationId);
    const { getInvitationById } = useSelector((state) => state.InvitationSlice);
    const userRestrictionData = useSelector((state) => state.VisitorSlice.getUserRestrictionVisitor);
    const [groupType, setGroupType] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [obverseImage, setObverseImage] = useState(null);
    const [reverseImage, setReverseImage] = useState(null);
    const vdDocument = useRef(null);
    const sdkInitialized = useRef(false);
    const sdkAssetsReady = useRef(null);
    const [loading, setLoading] = useState(false);
    const invitationId = getInvitationById?.invitation?.id;
    const selfieUrl = eventId
        ? getUrlParameters({ url: `${routes.eventDetails.selfieValidation}${userId}/${idProcess}/${eventId}`})
        : getUrlParameters({ url: routes.veridasIntegration.selfieValidation + id + "/", id:idProcess, guardFlowId});

    const failedUrl = getFailedUrl({ invitationId, guardFlowId, userId, eventId });
    const successUrl =  getSuccessUrl({ invitationId, guardFlowId, userId, eventId });
    const resolvedUserId  = userId ? userId: getInvitationById?.invitation?.visitorId;

    // Fetch user restrictions and prefetch SDK assets in parallel so reachability is validated before SDK init.
    useEffect(() => {
        dispatch(GetUserRestrictionVisitor());
        sdkAssetsReady.current = Promise.all([
            '/assets/document/workers/tf.worker.9f7c3b1a.js',
            '/assets/document/tensorflow/tfjs.08593238.umd.js',
            '/assets/document/tensorflow/wasm/tfjs-backend-wasm.wasm',
        ].map(async url => {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Asset not found: ${url}`);
            }

            const contentType = response.headers.get('content-type') || '';
            if (url.endsWith('.js') && contentType.includes('text/html')) {
                throw new Error(`Asset returned HTML instead of JS: ${url} (check static file routing)`);
            }

            return response;
        }));
    }, []);

    // Sync Redux document type selection to local state so the SDK load effect has a stable value to depend on.
    useEffect(() => {
        if(selectedDocumentType){
            setGroupType(selectedDocumentType.type);
            setDocumentType(selectedDocumentType.id);
        }
    }, [selectedDocumentType])

    const sdkStart = async () => {
        if (sdkInitialized.current) return;

        const target = targetRef.current;

        if (!target) return;

        const vdDocElement =
            target.querySelector('vd-document');

        if (!vdDocElement) return;

        sdkInitialized.current = true;

        try {
            vdDocument.current = vdDocElement;

            if (sdkAssetsReady.current) {
                await sdkAssetsReady.current;
            }

            await vdDocument.current.initializeSdk({
                setup: {
                    pathAssets: `${window.location.origin}/assets/document`,
                    language: getSdkLanguage(i18n.language),
                    documents: documentType,
                    logoShow: false,
                    deviceRotatedOnOrientation: DEVICE_ROTATED_ON_ORIENTATION.NONE,
                    closeButtonShow: true,
                    instructionsShow: true,
                    manualCaptureShow: true,
                    manualCaptureButtonDelay: 10000,
                    reviewShow: true,
                    detectionTimeout: 300000,
                    helpModalShow: true,
                    captureHeadingShow: true,
                    cancelButtonInstructionsShow: true,
                    instructionsHeadingShow: true,
                    reviewHeadingShow: true
                },
                styles: {
                    theme: {
                        brandPrimaryColor: primaryColor,
                    },
                    components: {
                        button: {
                            backgroundColorPrimary: primaryColor,
                            labelColorPrimary: whiteColor,
                            iconColorPrimary: whiteColor,
                            labelColorTertiary: primaryColor,
                            iconColorTertiary: primaryColor,
                        },
                        iconButton: {
                            iconColorTertiary: primaryColor,
                        },
                    },
                },
            });
        } catch (e) {
            sdkInitialized.current = false;
            console.error(t('vd_sdk_init_failed'), e);
        }
    };

    const onVDDocumentMounted = () => sdkStart();
    const onVDDocumentMountFailure = () => {};
    const onVDDocumentUnmounted = () => {};
    const onVDDocumentCloseButtonClicked = () => handleLeave();
    const onVDDocumentCapture = (e) => {
        const image = e.detail.legacyDetail;
        if (e.detail.legacyEvent === 'VDDocument_obverseDetection') {
            setObverseImage(image);
        } else if (e.detail.legacyEvent === 'VDDocument_reverseDetection') {
            setReverseImage(image);
        }
    };

    // Load the Veridas document SDK once documentType is resolved, register all VD events, and inject <vd-document>.
    // Deferred until documentType is set because initializeSdk requires the document ID in its setup config.
    useEffect(() => {
        if (!documentType) return;
        const target = targetRef.current;
        if (!target) return;

        const onDepsError = (e) => console.error(t('vd_dependencies_loading_error'), e.detail);
        const onBcnError = (e) => console.error(t('vd_bcn_model_error'), e.detail);
        const onGlareError = (e) => console.error(t('vd_glare_model_error'), e.detail);
        const onConfigError = (e) => console.error(t('vd_config_validation_error'), e.detail);
        window.addEventListener('VD_mounted', onVDDocumentMounted);
        window.addEventListener('VD_mountFailure', onVDDocumentMountFailure);
        window.addEventListener('VD_unmounted', onVDDocumentUnmounted);
        window.addEventListener('VD_capture', onVDDocumentCapture);
        window.addEventListener('VD_closeButtonClicked', onVDDocumentCloseButtonClicked);
        window.addEventListener('VD_dependenciesLoadingError', onDepsError);
        window.addEventListener('VD_bcnModelError', onBcnError);
        window.addEventListener('VD_glareModelError', onGlareError);
        window.addEventListener('VD_configValidationError', onConfigError);

        let active = true;
        loadVdSdk('vd-document', '/assets/document/vd-document.umd.js', { async: true })
            .then(async () => {
                if (!active || !targetRef.current) return;

                targetRef.current.innerHTML =
                    '<vd-document></vd-document>';
            })
            .catch((e) =>
                console.error(t('vd_sdk_load_failed'), e)
            );


        return () => {
            active = false;
            sdkInitialized.current = false;
            window.removeEventListener('VD_mounted', onVDDocumentMounted);
            window.removeEventListener('VD_mountFailure', onVDDocumentMountFailure);
            window.removeEventListener('VD_unmounted', onVDDocumentUnmounted);
            window.removeEventListener('VD_capture', onVDDocumentCapture);
            window.removeEventListener('VD_closeButtonClicked', onVDDocumentCloseButtonClicked);
            window.removeEventListener('VD_dependenciesLoadingError', onDepsError);
            window.removeEventListener('VD_bcnModelError', onBcnError);
            window.removeEventListener('VD_glareModelError', onGlareError);
            window.removeEventListener('VD_configValidationError', onConfigError);
            handleDestroy();
        };
    }, [documentType]);

    const handleDestroy = async () => {
        try {
            if (vdDocument.current && typeof vdDocument.current.destroy === 'function') {
                await vdDocument.current.destroy();
            }
            if (targetRef.current) {
                targetRef.current.innerHTML = '';
            }
        } catch (e) {
            console.warn(t('vd_document_cleanup_failed'), e);
        }
    }

    const handleFailed = async () => {
        setLoading(false);
        navigate(failedUrl);
        toast.error(t('error_validating_the_information_please_try_again'));
    }

    const handleSaveIne = async () => {
        setLoading(true);

        const obverse = {
            analysisType: FILE_TYPE_SIDE_MODEL.OBVERSE.identifier,
            documentImage: obverseImage,
            documentType: documentType,
            validationId: idProcess,
            userId: resolvedUserId,
        };
        await dispatch(ValidateObverseDocument(obverse)).then(({ payload: { data: { data } } }) => {
            if (data) {
                const reverse = {
                    analysisType: FILE_TYPE_SIDE_MODEL.REVERSE.identifier,
                    documentImage: reverseImage,
                    documentType: documentType,
                    validationId: idProcess,
                    userId: resolvedUserId,
                };
                dispatch(ValidateReverseDocument(reverse)).then(({ payload: { data: { data } } }) => {
                    if (data) {
                        documentValidationStatus(idProcess);
                    }else{
                        handleFailed();
                    }
                });
            }else{
                handleFailed();
            }
        });
    };

    const handleSavePassport = () => {
        setLoading(true);
        const obverse = {
            analysisType: FILE_TYPE_SIDE_MODEL.OBVERSE.identifier,
            documentImage: obverseImage,
            documentType: documentType,
            validationId: idProcess,
            userId: resolvedUserId,
        };
        dispatch(ValidateObverseDocument(obverse)).then(({ payload: { data: { data } } }) => {
            if (data) {
                documentValidationStatus(idProcess);
            } else {
                handleFailed();
            }
        });
    };

    const documentValidationStatus = async (validationId) => {
        const { payload: { data: { data, success } } } = await dispatch(VeridasServiceProcess({ validationId , userId:resolvedUserId }));
        if (success) {

            dispatch(setUserDataDocument(data));
            dispatch(setIsSuccessValidation(success));

            if(userRestrictionData?.biometricVisitor === true) {
                setLoading(false);
                navigate(selfieUrl);
            }else{
                if(eventId){
                    registerInEventVeridas({
                        dispatch,
                        resolvedUserId,
                        eventId,
                        setLoading,
                        t,
                        navigate,
                        successUrl,
                        data,
                        validationId
                    });
                    return;
                }
                setLoading(false);
                navigate(successUrl);
            }
        } else {
            await handleFailed();
        }
    };

    const destroyCameraAndSave = async () => {
        await handleDestroy();
        if (groupType === FILE_TYPE_MODEL.INE.id) {
            await handleSaveIne();
        } else if (groupType === FILE_TYPE_MODEL.PASSPORT.id) {
            await handleSavePassport();
        }
    }

    // Trigger save once the required sides are captured: INE needs both obverse and reverse; Passport needs obverse only.
    useEffect(() => {
        if (
            (groupType === FILE_TYPE_MODEL.INE.id && obverseImage && reverseImage) ||
            (groupType === FILE_TYPE_MODEL.PASSPORT.id && obverseImage)
        ) {
            destroyCameraAndSave();
        }
    }, [obverseImage, reverseImage]);

    useBackNavigation({getInvitationById, validationId: idProcess });
    const { handleLeave } = useHandleBeforeUnload({ getInvitationById ,validationId: idProcess });
    return loading ? <FullPageLoader /> :
        view({
            obverseImage,
            reverseImage,
            targetRef
        });

};

export default DocumentPage;
